import { AssetProcessingState, AssetType, type TechlineStatusCode } from '@packages/clients';

import type { TechlineAssetEntity } from '../entities';
import type {
    AssetErrorEntry,
    AssetReplacementCommand,
    AssetResult,
    AssetUpdateCommandParams,
    AssetUploadPayload,
    PresignedUploadParams,
} from './types';

/**
 * Build a payload that marks all currently-uploading assets as ERROR.
 * Used when the polling retry threshold is exceeded and the upload
 * is assumed to have failed.
 *
 * @param assets - assets to check (only those still in uploading status are included)
 * @returns the error entries to send to `StsBackendClient.updateAssets`
 * @example
 * const entries = buildAssetErrorPayload(uploadingAssets);
 */
export function buildAssetErrorPayload(assets: TechlineAssetEntity[]): AssetErrorEntry[] {
    return assets
        .filter((asset) => asset.status === 'uploading')
        .map((asset) => ({
            assetId: asset.id,
            percentUploaded: asset.progress,
            processingState: AssetProcessingState.Error,
        }));
}

/**
 * Build the POST body for replacing an existing asset's blob.
 * Used when the user re-uploads a file for an existing attachment.
 *
 * @param params - the original asset entity, update data, case number, and upload status
 * @returns the payload for `StsBackendClient.postAssets`
 * @example
 * const command = buildAssetReplacementCommand({ originalAsset, update, caseNumber, uploadStatus });
 */
export function buildAssetReplacementCommand(params: AssetUpdateCommandParams): AssetReplacementCommand {
    const { originalAsset, update, caseNumber, uploadStatus } = params;

    return {
        assets: [
            {
                assetDispositionId: originalAsset.disposition,
                assetTypeId: originalAsset.assetType as AssetType,
                caseNumber,
                fileName: update.name || originalAsset.name,
                hasOverlay: false,
                note: update.note ?? originalAsset.note,
                replacesAssetId: originalAsset.id,
            },
        ],
        techlinecasestatus: uploadStatus,
    };
}

/**
 * Build the POST body for creating a new techline asset upload.
 *
 * @param file - the file being uploaded
 * @param caseNumber - the case the asset belongs to
 * @param assetType - the determined asset type (Video, Image, or File)
 * @param uploadStatus - the case status to include in the request
 * @returns the payload for `StsBackendClient.postAssets`
 * @example
 * const payload = buildAssetUploadPayload(file, 'CASE-1', AssetType.Image, TechlineStatusCode.PENDING_TECHLINE);
 */
export function buildAssetUploadPayload(
    file: File,
    caseNumber: string,
    assetType: AssetType,
    uploadStatus: TechlineStatusCode
): AssetUploadPayload {
    return {
        assets: [
            {
                assetDispositionId: 'TechShare',
                assetTypeId: assetType,
                caseNumber,
                fileName: file.name,
                hasOverlay: false,
                name: file.name,
                note: '',
                restrdicted: false,
            },
        ],
        techlinecasestatus: uploadStatus,
    };
}

/**
 * Map a MIME type string to the corresponding AssetType enum value.
 * Falls back to File for anything that isn't video or image.
 *
 * @param mimeType - the file's MIME type (e.g. 'image/png', 'video/mp4')
 * @returns the matching AssetType enum value
 * @example
 * const type = determineAssetType('image/png'); // AssetType.Image
 */
export function determineAssetType(mimeType: string): AssetType {
    if (mimeType.includes('video')) {
        return AssetType.Video;
    }

    // SVG and HEIC are technically image MIME types but Techline uploads go to NorthStar's
    // Azure storage, which has no image processing pipeline for these formats. (QMR uploads
    // go through an AWS Lambda that handles conversion, but Techline bypasses that entirely.)
    // Classify as FILE so NorthStar stores them without attempting image processing.
    if (mimeType === 'image/svg+xml' || mimeType === 'image/heic' || mimeType === 'image/heif') {
        return AssetType.File;
    }

    if (mimeType.includes('image')) {
        return AssetType.Image;
    }
    return AssetType.File;
}

/**
 * Extract the `asset` object from each result in a `retrieveCaseAssets` response.
 * The API wraps each asset in a `{ asset }` envelope; this strips it.
 *
 * @param results - the raw result objects from `retrieveCaseAssets`
 * @returns the unwrapped asset objects
 * @example
 * const assets = unwrapAssetResults([{ asset: assetObj }]);
 */
export function unwrapAssetResults(results: AssetResult[]) {
    return results.map((result) => result.asset);
}

/**
 * Upload a file to a presigned URL using XMLHttpRequest. Uses XHR instead of fetch to support
 * upload progress tracking via the `onProgress` callback.
 *
 * Exception: this function performs network I/O (XHR upload), which violates the "helpers are pure
 * functions" rule. It lives here because it is called by two operations (UploadAsset and UpdateAsset)
 * as a shared upload step. Extracting it to its own operation would add IoC container overhead for
 * what is effectively a low-level upload utility, and it has no injected dependencies.
 *
 * @param params - the presigned URL, headers, file body, and optional progress callback
 * @param signal - optional abort signal for cancellation
 * @throws {Error} when the upload fails
 * @throws {DOMException} when the upload is aborted via the signal
 * @returns a promise that resolves when the upload completes
 * @example
 * await uploadAssetToPresignedUrl({ uploadUrl, headers, body: file, onProgress });
 */
export function uploadAssetToPresignedUrl(params: PresignedUploadParams, signal?: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        // Create an XHR to support upload progress events (not available via fetch)
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', params.uploadUrl, true);

        // Set required presigned upload headers
        Object.entries(params.headers).forEach(([header, value]) => {
            xhr.setRequestHeader(header, value);
        });

        // Override the content-type for HEIC files; the server expects the standardized
        // HEIF MIME type for its image processing pipeline to handle HEIC correctly.
        if ('type' in params.body && params.body.type === 'image/heic') {
            xhr.setRequestHeader('Content-Type', 'image/heif');
        }

        // Track upload progress and notify the caller
        xhr.upload.addEventListener('progress', (evt) => {
            if (evt.lengthComputable) {
                const progress = Math.round((evt.loaded * 100) / evt.total);
                params.onProgress?.(progress);
            }
        });

        // Resolve or reject the promise based on the XHR outcome
        xhr.addEventListener('load', () => {
            resolve();
        });
        xhr.addEventListener('error', () => {
            reject(new Error('Asset upload failed'));
        });

        // Wire up abort signal support
        if (signal) {
            signal.addEventListener('abort', () => {
                xhr.abort();
                reject(new DOMException('The operation was aborted.', 'AbortError'));
            });
        }

        // Send the file body
        xhr.send(params.body);
    });
}
